import Vue from "vue"
import Vuex from "vuex"
import {LanguageEnum} from "@/model/enum/language-enum"
import {ShipmentSender} from "@/model/entity/shipment/shipment-sender"
import {includes} from "lodash"
import {RoleEnum} from "@/model/enum/role-enum"

Vue.use(Vuex)

const state = {
  languagesFromBase: {},
  languages: [LanguageEnum.EN, LanguageEnum.HR, LanguageEnum.SI],
  authenticatedEmployee: null,
  isVerified: localStorage.getItem("isVerified") === "true",
  easyshipConfiguration: null,
  profiles: [],
  loading: false,
  packageVersion: process.env.VERSION
}

const mutations = {
  setLanguagesFromBase(state: any, languagesFromBase: any) {
    state.languagesFromBase = languagesFromBase
  },
  setAuthenticatedEmployee(state: any, authenticatedEmployee: any) {
    state.authenticatedEmployee = authenticatedEmployee
  },
  setLanguage(state: any, language: any) {
    state.language = language
  },
  setData(state: any, data: any) {
    state.data = data
  },
  setEasyshipConfiguration(state: any, easyshipConfiguration: any) {
    state.easyshipConfiguration = easyshipConfiguration
  },
  setProfiles(state: any, profiles: any) {
    state.profiles = profiles
  },
  activateLoading(state: any) {
    state.loading = true
  },
  deactivateLoading(state: any) {
    state.loading = false
  },
  setIsVerified(state: any, verified: boolean) {
    state.isVerified = verified
    localStorage.setItem("isVerified", verified.toString())
  }
}

const actions = {
  async getLanguagesFromBase({commit}: any) {
    try {
      const response = await Vue.$http.get("/language/get_languages")
      commit("setLanguagesFromBase", response.data)
    } catch (e) {
      throw e
    }
  },
  async login({commit}: any, {username, password}: any) {
    const response = await Vue.$http.post("/login", {
      username: username,
      remember_me: false,
      password: password
    })
    if (response.data.status === 401) {
      throw new Error(response.data.message)
    }
    commit("setAuthenticatedEmployee", response.data)
  },
  async logout({commit, state}: any) {
    const response = await Vue.$http.get("/employee/authenticated_employee")
    commit("setAuthenticatedEmployee", response.data)
    await Vue.$http.post("/logout", state.authenticatedEmployee)
    commit("setAuthenticatedEmployee", null)
  },
  async getAuthenticatedUser({commit}: any) {
    if (window.location.href.indexOf("new_password") > -1 && window.location.href.indexOf("t") > -1) {
      return
    }

    const response = await Vue.$http.get("/employee/authenticated_employee")
    commit("setAuthenticatedEmployee", response.data)
  },
  async getEasyshipConfiguration({commit}: any) {
    const response = await Vue.$http.get("/easyship_configuration")
    commit("setEasyshipConfiguration", response.data.easyshipConfiguration)
    commit("setProfiles", response.data.profiles)
  }
}

const getters = {
  isAuthenticated(state: any) {
    if (!state.authenticatedEmployee) {
      return false
    }

    const { twoFactorEnabled, twoFactorConfirmed } = state.authenticatedEmployee

    if (twoFactorEnabled) {
      return twoFactorConfirmed && localStorage.getItem("isVerified") === "true"
    }

    return true
  },
  employeeOfficeCountry(state: any) {
    if (!!state.authenticatedEmployee) {
      return state.authenticatedEmployee.office.country
    }
    return null
  },
  loggedUser(state: any) {
    return state.authenticatedEmployee
  },
  employeeHasRequiredRole(state: any) {
    return (requiredRoles: RoleEnum[]) => {
      if (state.authenticatedEmployee != null) {
        return includes(requiredRoles, state.authenticatedEmployee.role)
      } else {
        return false
      }
    }
  },
  menuEnabledForCompany(state: any) {
    return (meta: string) => {
      if (state.authenticatedEmployee != null) {
        return !(state.authenticatedEmployee.office.company.collectionRequestDisabled && meta === "collection_request")
      } else {
        return false
      }
    }
  },
  shipmentSender(state: any) {
    return (note: string) => {

      return new ShipmentSender(
        state.authenticatedEmployee.username,
        state.authenticatedEmployee.office.officeName,
        state.authenticatedEmployee.office.company.name,
        state.authenticatedEmployee.office.country.id,
        state.authenticatedEmployee.office.officeStreet,
        state.authenticatedEmployee.office.officeHouseNumber,
        "",
        state.authenticatedEmployee.office.officeCity,
        state.authenticatedEmployee.office.officeZip,
        state.authenticatedEmployee.office.officeTel,
        "",
        state.authenticatedEmployee.office.officeEmail,
        "",
        note
      )
    }
  },
  domesticCountryIso(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.domesticCountry.countryIso
    }
    return ""
  },
  codCountries(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.codCountries
    }
    return ""
  },
  roadExportCountries(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.roadExportCountries
    }
    return ""
  },
  businessUnitCode(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.businessUnit.bucode
    }
    return ""
  },
  depots(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.depots
    }
    return []
  },
  ranges(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.ranges
    }
    return {}
  },
  isProduction(state: any) {
    return exists(state.profiles) && notEmpty(state.profiles.find((profile: string) =>
        (profile === "prod_hr" || profile === "prod_si")))
  },
  colReqCountries(state: any) {
    if (exists(state.easyshipConfiguration)) {
      return state.easyshipConfiguration.collectionRequestCountries
    }
  },
  appVersion(state: any) {
   return state.packageVersion
  },
  environmentInfo(state: any, getters: any) {
    return {
      image: getters.domesticCountryIso === "SI" ? require("@/assets/image/slovenia.png") :
          require("@/assets/image/croatia.png"),
      env: getters.isProduction ? "Production" : "Test",
      active: !getters.isProduction
    }
  }
}

const store = new Vuex.Store({
  state,
  getters,
  actions,
  mutations
})

export default store

const startLoad = () => store.commit("activateLoading")
Vue.prototype.startLoad = startLoad
Vue.startLoad = startLoad

const endLoad = () => store.commit("deactivateLoading")
Vue.prototype.endLoad = endLoad
Vue.endLoad = endLoad
